import { viewportHeight, viewportWidth } from 'common/utilities/browser/viewport/viewport';
import { arraySum } from 'common/utilities/core/array';
import { getStore } from 'core/redux/store/configureStore';
import { ThemeVars } from 'core/utilities/theme';
import type { SequenceAlign } from 'sequenceAnalyser/api/sequenceAnalyserApiClient';
import { groupFeaturesByName } from 'sequenceAnalyser/utilities/features/features';
import type { VisibleSequencesFeatures } from 'sequenceAnalyser/utilities/sequenceAnalyser.types';
import { longestSequence,sequenceAlignLength } from 'sequenceAnalyser/utilities/sequenceAnalyser/sequenceAnalyser';

export const DEFAULT_VISIBLE_RESIDUES_CHUNKS = [0];
export const RESIDUE_PIXEL_WIDTH = 18; // width of a residue div within the UI
export const RESIDUE_PIXEL_HEIGHT = 21;
const RESIDUE_PIXEL_HEIGHT_WITH_FEATURES_AND_COLORS = 25;
const TABLE_ACTIONS_PIXEL_HEIGHT = 40;
export const RULER_PIXEL_HEIGHT = 24;

/** Height of the env bar + header + actions bar + ruler */
export function headerPixelHeight() {
  const { showAppHeaderEnv } = getStore().getState().ui;

  let top = ThemeVars.headerMenuHeight + TABLE_ACTIONS_PIXEL_HEIGHT + RULER_PIXEL_HEIGHT;
  if (showAppHeaderEnv) top += ThemeVars.headerEnvHeight;
  return top;
}

/** An element's size change as we zoom in/out  */
export function elementToScale(value: number, zoomLevel: number) {
  return value * (zoomLevel / 100);
}

export function sequencePixelHeight(hasColors: boolean, hasFeatures: boolean, zoomLevel = 100) {
  return elementToScale(hasColors && hasFeatures
    ? RESIDUE_PIXEL_HEIGHT_WITH_FEATURES_AND_COLORS
    : RESIDUE_PIXEL_HEIGHT,
  zoomLevel);
}

export function sequencePixelWidth(zoomLevel = 100) {
  return elementToScale(RESIDUE_PIXEL_WIDTH, zoomLevel);
}

/** Optimise based on the viewport height */
export function sequencesPerChunk(hasColors: boolean, hasFeatures: boolean) {
  const availableHeight = viewportHeight() - headerPixelHeight();
  return Math.round((availableHeight / sequencePixelHeight(hasColors, hasFeatures)) * 1.15);
}

/** Optimise based on the viewport width */
export function residuesPerChunk(sidebarPixelWidth: number) {
  const tableWidth = viewportWidth() - sidebarPixelWidth;
  return Math.round((tableWidth / RESIDUE_PIXEL_WIDTH) * 1.15);
}

/** Depending on the position and zoom level, we adjust the number of visible residues */
export function calculateVisibleResiduesChunks(
  horizontalScrollPosition: number, zoomLevel: number, sidebarPixelWidth: number,
) {
  // Init variables
  let visibleResiduesChunks = DEFAULT_VISIBLE_RESIDUES_CHUNKS;
  const rpc = residuesPerChunk(sidebarPixelWidth);

  // Calculate which residues (start, middle, end) are currently shown on the screen
  const offsetResidues = Math.round(elementToScale(sidebarPixelWidth, zoomLevel) / sequencePixelWidth(zoomLevel));
  const residueStart = Math.round(horizontalScrollPosition / sequencePixelWidth(zoomLevel));
  const residueEnd = residueStart + Math.round(window.innerWidth / sequencePixelWidth(zoomLevel)) - offsetResidues;
  const residueMiddle = residueStart + Math.round((residueEnd - residueStart) / 2);

  // Based on the residue in the middle of the screen,
  // calculate which chunk of residues is currently in the middle of the screen
  const chunkInCenter = Math.floor(residueMiddle / rpc);

  // Calculate where the chunks of residues before end and after start
  // This will help us to figure out if those chunks are shown to the user or not
  const chunkAfterResidueStart = ((chunkInCenter + 1) * rpc) + 1;
  let chunkBeforeResidueEnd = (chunkInCenter * rpc) - 1;
  if (chunkBeforeResidueEnd < 0) chunkBeforeResidueEnd = 0;

  // Always show chunk in the middle
  visibleResiduesChunks = [chunkInCenter];

  // If chunk before is partially shown, add it
  if (chunkBeforeResidueEnd > residueStart) {
    visibleResiduesChunks.push(chunkInCenter - 1);
  }

  // If chunk after is partiall show, add it
  if (chunkAfterResidueStart < residueEnd) {
    visibleResiduesChunks.push(chunkInCenter + 1);
  }

  return visibleResiduesChunks;
}

/**
 * Height of a sequences' chunk is based on the ([number of sequences] * [residues height]) + [expanded features]
 * Adjusted with the zoomLevel
 */
export function sequencesChunkHeight(
  sequences: SequenceAlign[], hasColors: boolean, hasFeatures: boolean,
  visibleSequencesFeatures: VisibleSequencesFeatures, expandedFeaturesForSequences: number[],
  zoomLevel: number,
) {
  const height = sequencePixelHeight(hasColors, hasFeatures);
  const heightWhenExpanded = sequencePixelHeight(hasColors, false);

  const totalHeight = arraySum(
    sequences.map((s) => {
      const expandedFeatures = expandedFeaturesForSequences.includes(s.id)
        ? Object.keys(groupFeaturesByName(visibleSequencesFeatures[s.id])).length
        : 0;

      if (expandedFeatures > 0) {
        return heightWhenExpanded + (expandedFeatures * heightWhenExpanded);
      }
      return height;
    }),
  );

  return elementToScale(totalHeight, zoomLevel);
}

/**
 * Width of a sequences' chunk is based on the [sequence with the highest number of residues] * [residues width]
 * Adjusted with the sidebar width and zoomLevel
 */
export function sequencesChunkWidth(sequences: SequenceAlign[], sidebarPixelWidth: number, zoomLevel: number) {
  return elementToScale(
    sequenceAlignLength(longestSequence(sequences)) * RESIDUE_PIXEL_WIDTH + sidebarPixelWidth,
    zoomLevel,
  );
}

export function visualisationsStartFrom(
  visualisationsHeight: number,
  sequences: SequenceAlign[],
  hasColors: boolean,
  hasFeatures: boolean,
  zoomLevel: number,
): ['top' | 'bottom', number] {
  const tableHeight = sequencesChunkHeight(sequences, hasColors, hasFeatures, {}, [], zoomLevel);
  const availableHeight = viewportHeight() - headerPixelHeight() - tableHeight;
  const adjustedVisualisationsHeight = elementToScale(visualisationsHeight, zoomLevel);

  if (availableHeight >= adjustedVisualisationsHeight) {
    // Starts below table
    return ['top', headerPixelHeight() + tableHeight];
  }

  // Stick to the bottom
  return ['bottom', 0];
}
